import parse, { Element } from 'html-react-parser';
import { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import ContentCard from '@alltrails/denali/components/ContentCard';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import { LinkTarget, Size } from '@alltrails/denali/types';
import getPlainTextFromReactNode from '../utils/getPlainTextFromReactNode';
import styles from './styles/styles.module.scss';

const removeEmTagContent = {
  replace: domNode => {
    const typedDomNode = domNode as Element;
    if (typedDomNode.attribs && typedDomNode.name === 'em') {
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <></>;
    }

    return undefined;
  }
};

type GuideCardProps = {
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement>) => void;
  externalControls?: { guideId: number | null; onFocus: (guideId: number | null) => void };
  photoUrl: ComponentProps<typeof Image>['src'];
  size?: Size<'sm' | 'md'>;
  tabIndex?: number;
  title: string;
  trailCount?: number;
  altText?: string;
  maxWidth?: number;
  href: string;
  target?: LinkTarget;
};

const GuideCard = ({
  className,
  externalControls,
  onClick,
  photoUrl,
  size = 'md',
  tabIndex,
  title,
  trailCount,
  altText,
  maxWidth,
  href,
  target = '_self'
}: GuideCardProps) => {
  const parsedTitle = parse(title);
  const imageAltText = altText || parse(title, removeEmTagContent); // only return the location of the guide
  const handleAddFocus = () => externalControls?.onFocus?.(externalControls.guideId);
  const handleRemoveFocus = () => externalControls?.onFocus?.(null);

  const extrasContent =
    size !== 'sm' && !!trailCount ? (
      <Typography variant="text100" color="secondary">
        <FormattedMessage
          defaultMessage="{trailCount, plural, one {# trail} other {# trails}}"
          values={{
            trailCount
          }}
        />
      </Typography>
    ) : undefined;

  return (
    <ContentCard
      href={href}
      onFocus={handleAddFocus}
      onBlur={handleRemoveFocus}
      onMouseEnter={handleAddFocus}
      onMouseLeave={handleRemoveFocus}
      tabIndex={tabIndex}
      className={className}
      onClick={onClick}
      target={target}
      size={size}
      imageSrc={photoUrl}
      imageAlt={getPlainTextFromReactNode(imageAltText)}
      imageMaxWidth={maxWidth}
      imageAspectRatio={1.5}
      title={parsedTitle}
      titleClassName={styles.guideCardTitle}
      extras={extrasContent}
      extrasClassName={styles.guideCardTrailCount}
    />
  );
};

export default GuideCard;
